// screens/CockpitCalendar.tsx — S1b: the cockpit's own Calendar surface
// (decision c7ee4a1b; spec COCKPIT-ASSISTANT-SPEC.md §S1b; mockup
// mockup-v10/Calendar.dc.html, plate 03). PERSONAL PACK ONLY — App.tsx's
// `case 'calendar'` branches here only when the pack is 'personal'; every
// other pack keeps the B17a agenda (Calendar.tsx) and the Personal tab's
// own embedded spread (CalendarSpread.tsx) exactly as they were.
//
// Built on what already exists (brief's own rule — nothing new on Core this
// chunk beyond the colour + tell_guests_required work already landed):
//   · GET /api/events?from&to&include_cancelled=true  (listAgenda) — every
//     event, Google's own `source` included; holds kept in their own array,
//     never merged in (B18g's rule, unchanged here).
//   · GET /api/google/connections — the account list + each one's
//     calendarsIn (summary, accessRole, colour — colour validated AGAIN
//     here, never trusted from the wire a second time without a check).
//   · PUT/POST /api/calendar/google/... (editGoogleCalendarEvent,
//     cancelGoogleCalendarEvent, undoGoogleEventChange,
//     proposeGoogleEventToOrganizer) for Google events; PATCH
//     /api/calendar/authored/:id (updateAuthoredEvent) for Vowric's own.
//
// ⚑ NO PATH NOTIFIES GUESTS WITHOUT HUAN'S OWN TAP. Every write that might
// carry sendUpdates goes through `applyEdit` below, which NEVER sets
// `tellGuests` itself — it is undefined until a person answers the "Tell
// the guests?" prompt, and that answer is read from the prompt's own two
// buttons, never defaulted. A 409 `tell_guests_required` (structured: a
// code and a guest count, not a sentence to match) pauses the write and
// asks; dismissing the prompt sends nothing.
//
// ⚑ EVERY EVENT RENDERS IN ITS OWN LOCAL TIME. `startLocal` + `timezone`
// come from the server (the B6a lesson, unchanged); this screen never
// re-derives a wall clock from the absolute instant, and a drag computes
// its new time by shifting the WALL CLOCK the event already carries, then
// sends that back with the event's own `timezone` — never the browser's.
//
// ⚑ COLOUR, VALIDATED A SECOND TIME. Core already drops anything that is
// not a strict `#rrggbb` to null (validateCalendarColor, calendarApi.ts);
// `safeHexColor` below runs the SAME check again before a value is ever
// used as a style, and only ever as a style VALUE — never concatenated
// into a class name, a CSS string, or innerHTML. Two independent gates.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  cancelGoogleCalendarEvent,
  createAuthoredEvent,
  editGoogleCalendarEvent,
  getGoogleConnections,
  listAgenda,
  proposeGoogleEventToOrganizer,
  undoGoogleEventChange,
  updateAuthoredEvent,
  type CalendarEventApi,
  type GoogleConnectionApi,
  type GoogleEventEditInput,
  type GoogleEventOutcome,
} from '../lib/api';
import { useCockpitWidth } from '../hooks/useCockpitWidth';
import { readCockpitPlace, writeCockpitPlace } from '../lib/cockpitPlace';
// MAIL-POLISH BRIEF addition (Huan, final, 2026-10-16) — "the existing
// authored-event create path (the same form/route the Personal tab uses,
// POST /api/calendar/authored)": the Personal tab's own `createAuthoredEvent`
// call and its wall-clock helpers (personalShared.tsx), reused rather than
// re-invented — see `NewEventSheet` below.
import { failureOf, localDate, localZone, nextHour } from './personalShared';
import './CockpitCalendar.css';

export interface CockpitCalendarProps {
  tenantId: string;
  /** The cockpit "+"'s relay for this screen (App.tsx's `cockpitCreate`) —
   * a bumped token, never a boolean, same reason Mail's own
   * `composeRequestToken` is one (see that prop's own comment). */
  newEventRequestToken?: number;
}

type View = 'day' | 'week' | 'month';

const HIDDEN_STORAGE_KEY = 'cockpit.calendar.hiddenCalendars.v1';
const HOUR_START = 7;
const HOUR_END = 21;
const PX_PER_HOUR = 48;
const PX_PER_MIN = PX_PER_HOUR / 60;
const SNAP_MIN = 15;
const VOWRIC_KEY = 'vowric';
/** ⚑ atlas-core review (10-15): plate 03 (mockup-v10/Calendar.dc.html)
 * draws the Vowric calendar in BLUE (#3987e5), not the cockpit's clay
 * accent — a literal hex, same posture as every other calendar's colour
 * here (Google's own swatches are static hexes too; this one is ours).
 * Follow the plate. */
const VOWRIC_COLOR = '#3987e5';

// ---------------------------------------------------------------------------
// Pure date helpers. Plain local-browser calendar days (navigating WHICH
// day/week/month is in view) — never confused with an event's own zone,
// which is read from the event itself wherever a time is shown or moved.
// ---------------------------------------------------------------------------

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}
function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}
/** Monday=0 .. Sunday=6 (brief: Month is Monday-first). */
function mondayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}
function mondayOf(d: Date): Date {
  return addDays(startOfDay(d), -mondayIndex(d));
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function naive(d: Date, hh: number, mm: number): string {
  return `${dateKey(d)}T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// H2 (COCKPIT-HOME-COLUMNS-SPEC.md §H2, review round 2) — "per column"
// means every column whose screen HAS a place, not Mail alone: Calendar's
// own place is its view (Day/Week/Month) and the date it's showing.
// `anchorKey` is `dateKey(anchor)` (local calendar day, no time-of-day,
// no zone) — parsed back the SAME way `naive()` above writes a wall-clock
// string: no trailing 'Z', so the Date constructor reads it as LOCAL
// midnight, never UTC midnight (which would read back as the day before
// in a negative-offset zone).
// ---------------------------------------------------------------------------
interface CalendarPlace {
  view: View;
  anchorKey: string;
}

function readCalendarPlace(): CalendarPlace | null {
  return readCockpitPlace<CalendarPlace>('calendar');
}

function parseDateKey(key: string): Date | null {
  const d = new Date(`${key}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : startOfDay(d);
}

// ---------------------------------------------------------------------------
// Colour: validated a second time, used only as a style value.
// ---------------------------------------------------------------------------

/** The SAME shape Core's validateCalendarColor accepts — a strict 6-digit
 * hex triplet, nothing else. Returns the fallback token (never the raw,
 * unvalidated input) for anything else, including a CSS-injection attempt. */
export function safeHexColor(raw: string | null | undefined, fallback = 'var(--ink-3)'): string {
  return typeof raw === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw) ? raw : fallback;
}

// ---------------------------------------------------------------------------
// The calendar directory: every Google calendar (grouped by account) plus
// the one Vowric entry, each with a stable key events are matched against.
// ---------------------------------------------------------------------------

export interface CalEntry {
  key: string;
  label: string;
  color: string;
  tag: 'Google' | 'Vowric';
  accountEmail: string | null;
  readOnly: boolean;
  writable: boolean;
}

function googleKey(connectionId: string, calendarId: string): string {
  return `${connectionId}:${calendarId}`;
}

function buildDirectory(connections: GoogleConnectionApi[]): CalEntry[] {
  const out: CalEntry[] = [];
  for (const c of connections) {
    if (c.status === 'revoked' || !c.features.includes('calendar')) continue;
    for (const cal of c.calendarsIn) {
      const writable = cal.accessRole === 'owner' || cal.accessRole === 'writer';
      out.push({
        key: googleKey(c.id, cal.id),
        label: cal.summary,
        color: safeHexColor(cal.color, 'var(--ink-3)'),
        tag: 'Google',
        accountEmail: c.email,
        readOnly: !writable,
        writable,
      });
    }
  }
  out.push({ key: VOWRIC_KEY, label: 'Vowric', color: VOWRIC_COLOR, tag: 'Vowric', accountEmail: null, readOnly: false, writable: true });
  return out;
}

function keyOf(e: CalendarEventApi): string {
  if (e.source === 'google' && e.googleConnectionId && e.externalCalendarId) return googleKey(e.googleConnectionId, e.externalCalendarId);
  return VOWRIC_KEY;
}

function loadHidden(): Set<string> {
  try {
    const raw = window.localStorage.getItem(HIDDEN_STORAGE_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}
function saveHidden(hidden: Set<string>): void {
  try {
    window.localStorage.setItem(HIDDEN_STORAGE_KEY, JSON.stringify([...hidden]));
  } catch {
    // Unticking is a view-only convenience; a browser that refuses storage
    // (private mode, quota) just keeps showing everything. Never a crash.
  }
}

// ---------------------------------------------------------------------------
// Event time maths — read the event's OWN wall clock, never the browser's.
// ---------------------------------------------------------------------------

interface EventSpan {
  dateKey: string;
  startMin: number; // minutes after local midnight, in the event's OWN zone
  durationMin: number;
  allDay: boolean;
}

function spanOf(e: CalendarEventApi): EventSpan | null {
  if (!e.startLocal) return null;
  if (e.allDay) return { dateKey: e.startLocal.slice(0, 10), startMin: 0, durationMin: 0, allDay: true };
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(e.startLocal);
  if (!m) return null;
  const startMs = Date.parse(e.startsAt);
  const endMs = e.endsAt ? Date.parse(e.endsAt) : startMs + 30 * 60_000;
  const durationMin = Math.max(15, Math.round((endMs - startMs) / 60_000));
  return { dateKey: m[1]!, startMin: Number(m[2]) * 60 + Number(m[3]), durationMin, allDay: false };
}

function isWritable(e: CalendarEventApi, dir: Map<string, CalEntry>): boolean {
  if (e.source === 'invite') return false; // someone else's — read-only here, as B17a always treated it
  const entry = dir.get(keyOf(e));
  return entry ? entry.writable : e.source === 'authored';
}

// ---------------------------------------------------------------------------
// ⚑ atlas-core review round 3 (10-15): "a line is either fully shown or not
// rendered" — a round-2 fix picked titleLines/showTime from a HAND-ESTIMATED
// line-height (13px × 1.2) and never accounted for how flexbox actually
// sizes a `-webkit-line-clamp` box once its flex-column parent is shorter
// than the clamped box's own content height: the clamp box still wants its
// full 2-line height, the TIME item after it still wants its own height,
// and the PARENT's `overflow:hidden` then clips whatever doesn't fit —
// through the middle of the title's second line, with the time landing on
// top of it. The actual fix is two parts: `flex-shrink: 0` on both spans
// (CockpitCalendar.css) so flexbox never tries to squeeze either one below
// its natural size, and MEASURING the real rendered metrics ONCE (a hidden
// probe built from the exact same classes/markup CSS already governs) so
// the four candidate layouts below are compared against what the browser
// ACTUALLY renders a line/gap/padding as, never a guessed constant — a
// font-metrics or CSS change elsewhere can never silently reopen this bug.
interface EventBlockMetrics {
  titleLineH: number;
  timeLineH: number;
  gapPx: number;
  padV: number;
}

let cachedMetrics: EventBlockMetrics | null = null;

function measureEventBlockMetrics(): EventBlockMetrics {
  if (cachedMetrics) return cachedMetrics;
  const probe = document.createElement('div');
  probe.className = 'cockcal__event';
  Object.assign(probe.style, { position: 'fixed', visibility: 'hidden', pointerEvents: 'none', left: '-9999px', top: '0', width: '120px', height: 'auto' });
  const title = document.createElement('span');
  title.className = 'cockcal__event-title';
  title.textContent = 'X'; // one line, unclamped, to read its natural line height
  const time = document.createElement('span');
  time.className = 'cockcal__event-time';
  time.textContent = '00:00';
  probe.appendChild(title);
  probe.appendChild(time);
  document.body.appendChild(probe);
  const probeStyle = getComputedStyle(probe);
  const padV = (parseFloat(probeStyle.paddingTop) || 0) + (parseFloat(probeStyle.paddingBottom) || 0);
  const gapPx = parseFloat(probeStyle.rowGap || probeStyle.gap || '0') || 0;
  const titleLineH = title.getBoundingClientRect().height || 16;
  const timeLineH = time.getBoundingClientRect().height || 14;
  document.body.removeChild(probe);
  cachedMetrics = { titleLineH, timeLineH, gapPx, padV };
  return cachedMetrics;
}

/** Which of the four layouts (2-line title + time, 2-line title alone,
 * 1-line title + time, 1-line title alone) fits the block's REAL content
 * height — title always wins the room first; time is the first and only
 * thing ever dropped; a 1px safety margin absorbs sub-pixel rounding so
 * nothing is ever a hair too tall and clipped mid-line. */
function eventLayoutFor(blockHeightPx: number): { titleLines: 1 | 2; showTime: boolean } {
  const { titleLineH, timeLineH, gapPx, padV } = measureEventBlockMetrics();
  const contentH = blockHeightPx - padV - 1; // the 1px margin
  const twoLineWithTime = 2 * titleLineH + gapPx + timeLineH;
  const twoLineAlone = 2 * titleLineH;
  const oneLineWithTime = titleLineH + gapPx + timeLineH;
  if (contentH >= twoLineWithTime) return { titleLines: 2, showTime: true };
  if (contentH >= twoLineAlone) return { titleLines: 2, showTime: false };
  if (contentH >= oneLineWithTime) return { titleLines: 1, showTime: true };
  return { titleLines: 1, showTime: false };
}

// ---------------------------------------------------------------------------
// Pending writes: tell-the-guests prompt + the last receipt (with Undo).
// ---------------------------------------------------------------------------

interface PendingAsk {
  eventId: string;
  patch: GoogleEventEditInput;
  guests: number;
  /** The SERVER's own sentence (voice canon §2.4: "show the server's own
   * sentence, never a vaguer second copy") — shown verbatim; this screen
   * never composes its own paraphrase of why it is asking. */
  message: string;
  /** What the drag/edit already shows on screen, kept until answered or dismissed. */
  preview: { startMin: number; durationMin: number } | null;
}
interface Receipt {
  text: string;
  changeId: string | null;
}
interface Banner {
  text: string;
  tone: 'error' | 'info';
}

interface TellGuestsRefusal {
  guests: number;
  message: string;
}

function tellGuestsRefusalFrom(err: unknown): TellGuestsRefusal | null {
  if (err instanceof ApiError && err.code === 'tell_guests_required') {
    const body = err.body as { error?: { guests?: unknown; message?: unknown } } | undefined;
    const n = body?.error?.guests;
    const msg = body?.error?.message;
    return {
      guests: typeof n === 'number' ? n : 0,
      message: typeof msg === 'string' && msg ? msg : err.message,
    };
  }
  return null;
}

async function applyEdit(ev: CalendarEventApi, patch: GoogleEventEditInput, tenantId: string): Promise<{ event: GoogleEventOutcome }> {
  if (ev.source === 'google') return editGoogleCalendarEvent(tenantId, ev.id, patch);
  const r = await updateAuthoredEvent(tenantId, ev.id, {
    title: patch.title,
    start: patch.start,
    end: patch.end,
    timezone: patch.timezone,
    allDay: patch.allDay,
  });
  return {
    event: {
      outcome: 'done',
      id: r.id,
      changeId: '',
      sendUpdates: 'none',
      writtenThroughOrganizer: null,
      guests: [],
      receipt: `Changed "${r.title ?? 'event'}"`,
    },
  };
}

// ===========================================================================

export function CockpitCalendar({ tenantId, newEventRequestToken = 0 }: CockpitCalendarProps) {
  const cockpitWidth = useCockpitWidth(); // 'bar' (<900, folded) | 'narrow' (900-1279) | 'wide' (>=1280)
  const folded = cockpitWidth === 'bar';
  const narrow = cockpitWidth === 'narrow';

  // Week is the brief's own default EXCEPT when the screen opens folded
  // (<900, phone-shaped): there, Day is the default (brief's own words) —
  // read once at mount, same pattern as every other width-aware initial
  // state in this file. H2 — a kept place (view + anchor day) overrides
  // that default when there is one; a fresh tenant, or a blocked/corrupt
  // store, reads exactly the old defaults.
  const [view, setView] = useState<View>(() => readCalendarPlace()?.view ?? (cockpitWidth === 'bar' ? 'day' : 'week'));
  const [anchor, setAnchor] = useState<Date>(() => {
    const key = readCalendarPlace()?.anchorKey;
    return (key && parseDateKey(key)) || startOfDay(new Date());
  });
  const [events, setEvents] = useState<CalendarEventApi[] | null>(null);
  const [connections, setConnections] = useState<GoogleConnectionApi[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [hidden, setHidden] = useState<Set<string>>(() => loadHidden());
  const [stripSheetOpen, setStripSheetOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<{ title: string; location: string }>({ title: '', location: '' });
  const [drag, setDrag] = useState<{ eventId: string; mode: 'move' | 'resize'; startMin: number; durationMin: number } | null>(null);
  const [ask, setAsk] = useState<PendingAsk | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [busy, setBusy] = useState(false);
  // MAIL-POLISH BRIEF addition — the cockpit "+" on Calendar opens New
  // event, prefilled with the day in view (`anchor`) at the next hour. No
  // guests field in this chunk (the brief's own words).
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventDate, setNewEventDate] = useState(() => localDate(new Date()));
  const [newEventTime, setNewEventTime] = useState(() => nextHour());
  const [newEventBusy, setNewEventBusy] = useState(false);
  const [newEventError, setNewEventError] = useState<string | null>(null);
  const lastNewEventToken = useRef(newEventRequestToken);

  const dragRef = useRef<{ pointerId: number; startClientY: number; startMin: number; durationMin: number } | null>(null);

  const today = useMemo(() => startOfDay(new Date()), []);

  // ---- load ---------------------------------------------------------------
  const range = useMemo(() => {
    const monday = mondayOf(anchor);
    if (view === 'day') return { from: addDays(anchor, -1), to: addDays(anchor, 2) };
    if (view === 'week') return { from: addDays(monday, -1), to: addDays(monday, 8) };
    const gridStart = mondayOf(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    return { from: addDays(gridStart, -1), to: addDays(gridStart, 43) };
  }, [view, anchor]);

  useEffect(() => {
    let alive = true;
    setLoadError(null);
    listAgenda(tenantId, { from: range.from.toISOString(), to: range.to.toISOString(), includeCancelled: true })
      .then((r) => {
        if (alive) setEvents(r.events);
      })
      .catch((err) => {
        if (alive) setLoadError(err instanceof ApiError ? err.message : 'Could not load the calendar');
      });
    return () => {
      alive = false;
    };
  }, [tenantId, range.from.getTime(), range.to.getTime(), reloadKey]);

  // H2 — keep the place: every view/date change writes it straight back
  // out. Runs on mount too (writing back the SAME values the lazy initial
  // state above just read) — harmless, since (unlike Mail's own list
  // scroll) there is no async gap here: `view`/`anchor` ARE the state from
  // the very first render, nothing has to wait for a later read to apply.
  useEffect(() => {
    writeCockpitPlace('calendar', { view, anchorKey: dateKey(anchor) });
  }, [view, anchor]);

  // MAIL-POLISH BRIEF addition — a changed token (never the FIRST value
  // seen — mirrors Mail's own composeRequestToken guard) opens a fresh New
  // event sheet, prefilled with the day in view at the next hour.
  useEffect(() => {
    if (newEventRequestToken === lastNewEventToken.current) return;
    lastNewEventToken.current = newEventRequestToken;
    setNewEventTitle('');
    setNewEventDate(localDate(anchor));
    setNewEventTime(nextHour());
    setNewEventError(null);
    setNewEventOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- anchor read for the prefill only, at the moment of the tap
  }, [newEventRequestToken]);

  useEffect(() => {
    let alive = true;
    getGoogleConnections(tenantId)
      .then((r) => {
        if (alive) setConnections(r.connections);
      })
      .catch(() => undefined); // the Vowric entry alone is still a usable screen
    return () => {
      alive = false;
    };
  }, [tenantId, reloadKey]);

  const directory = useMemo(() => buildDirectory(connections), [connections]);
  const dirByKey = useMemo(() => new Map(directory.map((d) => [d.key, d])), [directory]);
  const dirByAccount = useMemo(() => {
    const groups = new Map<string, CalEntry[]>();
    for (const d of directory) {
      if (d.tag !== 'Google') continue;
      const list = groups.get(d.accountEmail!) ?? [];
      list.push(d);
      groups.set(d.accountEmail!, list);
    }
    return groups;
  }, [directory]);
  const vowricEntry = directory.find((d) => d.key === VOWRIC_KEY)!;

  const visibleEvents = useMemo(() => (events ?? []).filter((e) => !hidden.has(keyOf(e))), [events, hidden]);
  const selected = visibleEvents.find((e) => e.id === selectedId) ?? null;

  function toggleHidden(key: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveHidden(next);
      return next;
    });
  }

  // ---- navigation -----------------------------------------------------
  function goToday() {
    setAnchor(startOfDay(new Date()));
  }
  function step(dir: 1 | -1) {
    if (view === 'day') setAnchor((d) => addDays(d, dir));
    else if (view === 'week') setAnchor((d) => addDays(d, dir * 7));
    else setAnchor((d) => new Date(d.getFullYear(), d.getMonth() + dir, 1));
  }
  function jumpTo(d: Date) {
    setAnchor(startOfDay(d));
  }
  function openDay(d: Date) {
    setAnchor(startOfDay(d));
    setView('day');
  }

  const title = useMemo(() => {
    if (view === 'month') return anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (view === 'day') return anchor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const monday = mondayOf(anchor);
    const sunday = addDays(monday, 6);
    return `${monday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${sunday.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }, [view, anchor]);

  // ---- writes -----------------------------------------------------------

  function clearAskAndRollback() {
    setAsk(null);
    setDrag(null);
  }

  async function send(ev: CalendarEventApi, patch: GoogleEventEditInput, previewWhileAsking: { startMin: number; durationMin: number } | null) {
    setBusy(true);
    try {
      const r = await applyEdit(ev, patch, tenantId);
      setBanner(null);
      setAsk(null);
      setDrag(null);
      if (r.event.outcome === 'done' && r.event.changeId) setReceipt({ text: r.event.receipt, changeId: r.event.changeId });
      else if (r.event.outcome === 'done') setReceipt({ text: r.event.receipt, changeId: null });
      setReloadKey((n) => n + 1);
    } catch (err) {
      const refusal = tellGuestsRefusalFrom(err);
      if (refusal) {
        setAsk({ eventId: ev.id, patch, guests: refusal.guests, message: refusal.message, preview: previewWhileAsking });
        return;
      }
      setDrag(null);
      setAsk(null);
      setBanner({ text: err instanceof ApiError && err.serverMessage ? err.serverMessage : 'Could not save that change — nothing changed.', tone: 'error' });
    } finally {
      setBusy(false);
    }
  }

  function answerTellGuests(tellGuests: boolean) {
    if (!ask) return;
    const ev = (events ?? []).find((e) => e.id === ask.eventId);
    if (!ev) {
      setAsk(null);
      return;
    }
    void send(ev, { ...ask.patch, tellGuests }, ask.preview);
  }

  async function undo() {
    if (!receipt?.changeId) return;
    setBusy(true);
    try {
      const r = await undoGoogleEventChange(tenantId, receipt.changeId);
      setReceipt(null);
      setBanner(null);
      setReloadKey((n) => n + 1);
      if (r.event.outcome === 'done') setBanner({ text: r.event.receipt, tone: 'info' });
    } catch (err) {
      const refusal = tellGuestsRefusalFrom(err);
      if (refusal) {
        setBanner({ text: 'Undo needs an answer to "Tell the guests?" — open the event and try again.', tone: 'error' });
      } else {
        setBanner({ text: err instanceof ApiError && err.serverMessage ? err.serverMessage : 'Could not undo that.', tone: 'error' });
      }
    } finally {
      setBusy(false);
    }
  }

  // ---- drag to move / resize --------------------------------------------

  function beginDrag(e: PointerEvent, ev: CalendarEventApi, mode: 'move' | 'resize') {
    if (!isWritable(ev, dirByKey)) return;
    const span = spanOf(ev);
    if (!span || span.allDay) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = { pointerId: e.pointerId, startClientY: e.clientY, startMin: span.startMin, durationMin: span.durationMin };
    setDrag({ eventId: ev.id, mode, startMin: span.startMin, durationMin: span.durationMin });

    function onMove(ev2: PointerEvent) {
      const d = dragRef.current;
      if (!d || ev2.pointerId !== d.pointerId) return;
      const deltaMin = Math.round((ev2.clientY - d.startClientY) / PX_PER_MIN / SNAP_MIN) * SNAP_MIN;
      if (mode === 'move') {
        const next = Math.max(0, Math.min(24 * 60 - d.durationMin, d.startMin + deltaMin));
        setDrag({ eventId: ev.id, mode, startMin: next, durationMin: d.durationMin });
      } else {
        const next = Math.max(SNAP_MIN, d.durationMin + deltaMin);
        setDrag({ eventId: ev.id, mode, startMin: d.startMin, durationMin: next });
      }
    }
    function onUp(ev2: PointerEvent) {
      const d = dragRef.current;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (!d || ev2.pointerId !== d.pointerId) return;
      dragRef.current = null;
      const finalState = { eventId: ev.id, mode, startMin: 0, durationMin: 0 };
      setDrag((cur) => {
        finalState.startMin = cur?.startMin ?? d.startMin;
        finalState.durationMin = cur?.durationMin ?? d.durationMin;
        return cur;
      });
      // Nothing moved — no write needed.
      if (finalState.startMin === d.startMin && finalState.durationMin === d.durationMin) {
        setDrag(null);
        return;
      }
      const span = spanOf(ev)!;
      const date = new Date(`${span.dateKey}T00:00:00`);
      const startDate = new Date(date);
      startDate.setMinutes(finalState.startMin);
      const endDate = new Date(date);
      endDate.setMinutes(finalState.startMin + finalState.durationMin);
      const patch: GoogleEventEditInput = {
        start: naive(startDate, startDate.getHours(), startDate.getMinutes()),
        end: naive(endDate, endDate.getHours(), endDate.getMinutes()),
        timezone: ev.timezone ?? 'UTC',
      };
      void send(ev, patch, { startMin: finalState.startMin, durationMin: finalState.durationMin });
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  // ---- derived view data --------------------------------------------------

  const weekDays = useMemo(() => {
    const monday = mondayOf(view === 'day' ? anchor : anchor);
    if (view === 'day') return [anchor];
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [view, anchor]);

  const hours = useMemo(() => Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i), []);

  function eventsOn(day: Date) {
    const key = dateKey(day);
    return visibleEvents
      .filter((e) => {
        const span = spanOf(e);
        return span && span.dateKey === key;
      })
      .map((e) => ({ e, span: spanOf(e)!, dir: dirByKey.get(keyOf(e)) }));
  }

  const nowMin = useMemo(() => {
    const n = new Date();
    return n.getHours() * 60 + n.getMinutes();
  }, [reloadKey]); // refreshed whenever the screen reloads; good enough for a static shot

  // ⚑ Atlas review (2026-10-16): a month's own days never spill past 6
  // rows (Mon-first), but MOST months are done in 5 — a 6th row that is
  // entirely next-month padding is dropped, so the grid only ever carries
  // as many rows as the shown month actually needs. This matters because
  // MonthGrid's own rows now SHARE the screen's available height evenly
  // (`minmax(0, 1fr)`, below) — a wasted 6th padding row would otherwise
  // squeeze every REAL row shorter for nothing. A month that genuinely
  // needs 6 (November 2026: opens on a Sunday) keeps all 42; that case is
  // what the 1fr sizing itself has to fit without clipping, not this trim.
  const monthCells = useMemo(() => {
    if (view !== 'month') return [];
    const gridStart = mondayOf(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    const all42 = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    const sixthRowIsAllOutside = all42.slice(35, 42).every((d) => d.getMonth() !== anchor.getMonth());
    return sixthRowIsAllOutside ? all42.slice(0, 35) : all42;
  }, [view, anchor]);

  // ---------------------------------------------------------------------

  if (loadError) {
    return (
      <div class="cockcal cockcal--error">
        <p>{loadError}</p>
      </div>
    );
  }

  const stripVisible = !folded && !narrow;
  const stripAsSheet = folded || narrow;
  // ⚑ Fold-open (atlas, 2026-10-15): 900px total − the 320px Atlas column =
  // 580px of content. With the Mist strip already collapsed (above), a
  // 56px hour gutter leaves (580-56)/7 ≈ 74.9px per Week day-column — short
  // of the 80px floor. Trimming the gutter to 16px (compact hour labels,
  // "7" not "07:00") gets (580-16)/7 ≈ 80.6px: the grid clears 80px without
  // moving the Atlas column or the strip's own collapse. Folded (<900) is
  // one pane — a slightly wider 28px gutter there since Day view has no
  // column-count pressure.
  const compactGutter = narrow || folded;

  /** MAIL-POLISH BRIEF addition — "the existing authored-event create path
   * (the same form/route the Personal tab uses, POST /api/calendar/authored)".
   * No guests field in this chunk (the brief's own words); no repeat/
   * reminder/visibility choices either — Personal's own EventSheet keeps
   * those for the fuller form; this is the quick "+" door, title + when. */
  async function createNewEvent(e: Event) {
    e.preventDefault();
    if (newEventBusy) return;
    const title = newEventTitle.trim();
    if (!title) {
      setNewEventError('An event needs a title.');
      return;
    }
    setNewEventBusy(true);
    setNewEventError(null);
    try {
      await createAuthoredEvent(tenantId, {
        title,
        start: `${newEventDate}T${newEventTime}`,
        allDay: false,
        timezone: localZone(),
      });
      setNewEventOpen(false);
      setReloadKey((n) => n + 1);
    } catch (err) {
      setNewEventError(failureOf(err));
      setNewEventBusy(false);
    }
  }

  return (
    <div class={`cockcal ${folded ? 'cockcal--folded' : narrow ? 'cockcal--narrow' : 'cockcal--wide'}`}>
      {stripVisible ? (
        <CalendarStrip
          anchor={anchor}
          today={today}
          onJump={jumpTo}
          dirByAccount={dirByAccount}
          vowricEntry={vowricEntry}
          hidden={hidden}
          onToggle={toggleHidden}
        />
      ) : null}
      <div class="cockcal__main">
        <header class="cockcal__header">
          {stripAsSheet ? (
            <button type="button" class="cockcal__strip-open" aria-label="Open calendars" onClick={() => setStripSheetOpen(true)}>
              ☰
            </button>
          ) : null}
          <div class="cockcal__navbtns">
            <button type="button" aria-label="Previous" onClick={() => step(-1)}>
              ‹
            </button>
            <button type="button" onClick={goToday}>
              Today
            </button>
            <button type="button" aria-label="Next" onClick={() => step(1)}>
              ›
            </button>
          </div>
          <h1 class="cockcal__title">{title}</h1>
          <div class="cockcal__views" role="group" aria-label="View">
            {(['day', 'week', 'month'] as View[]).map((v) => (
              <button key={v} type="button" class={v === view ? 'is-active' : ''} aria-pressed={v === view} onClick={() => setView(v)}>
                {v === 'day' ? 'Day' : v === 'week' ? 'Week' : 'Month'}
              </button>
            ))}
          </div>
        </header>

        {banner ? (
          <div class={`cockcal__banner cockcal__banner--${banner.tone}`} role="alert">
            <span>{banner.text}</span>
            <button type="button" onClick={() => setBanner(null)} aria-label="Dismiss">
              ×
            </button>
          </div>
        ) : null}

        {view === 'month' ? (
          <MonthGrid
            cells={monthCells}
            month={anchor.getMonth()}
            today={today}
            eventsOn={eventsOn}
            dirByKey={dirByKey}
            onPickDay={openDay}
            onPickEvent={setSelectedId}
          />
        ) : (
          <TimeGrid
            days={weekDays}
            today={today}
            hours={hours}
            nowMin={nowMin}
            eventsOn={eventsOn}
            drag={drag}
            selectedId={selectedId}
            onPickEvent={setSelectedId}
            onPickDay={view === 'week' ? openDay : undefined}
            onBeginDrag={beginDrag}
            compactGutter={compactGutter}
          />
        )}
      </div>

      {selected ? (
        <DetailBar
          event={selected}
          entry={dirByKey.get(keyOf(selected)) ?? null}
          writable={isWritable(selected, dirByKey)}
          busy={busy}
          editing={editing}
          draft={editDraft}
          onStartEdit={() => {
            setEditDraft({ title: selected.title ?? '', location: selected.location ?? '' });
            setEditing(true);
          }}
          onCancelEdit={() => setEditing(false)}
          onDraftChange={setEditDraft}
          onSaveEdit={() => {
            setEditing(false);
            void send(selected, { title: editDraft.title, location: editDraft.location }, null);
          }}
          onCancelEvent={
            selected.source === 'google' && isWritable(selected, dirByKey)
              ? () => {
                  void (async () => {
                    setBusy(true);
                    try {
                      const r = await cancelGoogleCalendarEvent(tenantId, selected.id);
                      setReceipt(r.event.outcome === 'done' ? { text: r.event.receipt, changeId: r.event.changeId } : null);
                      setReloadKey((n) => n + 1);
                    } catch (err) {
                      const refusal = tellGuestsRefusalFrom(err);
                      if (refusal) setAsk({ eventId: selected.id, patch: {}, guests: refusal.guests, message: refusal.message, preview: null });
                      else setBanner({ text: err instanceof ApiError && err.serverMessage ? err.serverMessage : 'Could not cancel that.', tone: 'error' });
                    } finally {
                      setBusy(false);
                    }
                  })();
                }
              : undefined
          }
          onProposeToOrganizer={
            selected.source === 'google' && !isWritable(selected, dirByKey)
              ? () => {
                  void proposeGoogleEventToOrganizer(tenantId, selected.id, {}).then((r) =>
                    setBanner({ text: r.proposal.receipt, tone: 'info' })
                  );
                }
              : undefined
          }
          onClose={() => {
            setSelectedId(null);
            setEditing(false);
          }}
        />
      ) : null}

      {ask ? (
        <TellGuestsPrompt
          message={ask.message}
          busy={busy}
          onTell={() => answerTellGuests(true)}
          onDontTell={() => answerTellGuests(false)}
          onDismiss={clearAskAndRollback}
        />
      ) : null}

      {receipt && !ask ? (
        <div class="cockcal__receipt" role="status">
          <span>{receipt.text}</span>
          {receipt.changeId ? (
            <button type="button" class="cockcal__undo" onClick={() => void undo()}>
              Undo
            </button>
          ) : null}
          <button type="button" aria-label="Dismiss" onClick={() => setReceipt(null)}>
            ×
          </button>
        </div>
      ) : null}

      {stripSheetOpen ? (
        <div class="cockcal__sheet-overlay" role="dialog" aria-label="Calendars">
          <div class="cockcal__sheet">
            <button type="button" class="cockcal__sheet-close" onClick={() => setStripSheetOpen(false)}>
              Done
            </button>
            <CalendarStrip
              anchor={anchor}
              today={today}
              onJump={(d) => {
                jumpTo(d);
                setStripSheetOpen(false);
              }}
              dirByAccount={dirByAccount}
              vowricEntry={vowricEntry}
              hidden={hidden}
              onToggle={toggleHidden}
            />
          </div>
        </div>
      ) : null}

      {newEventOpen ? (
        <div class="cockcal__newevent-overlay" role="dialog" aria-label="New event">
          <form class="cockcal__newevent-sheet" onSubmit={createNewEvent}>
            <h2 class="cockcal__newevent-title">New event</h2>
            <label class="cockcal__newevent-field">
              <span>Title</span>
              <input
                type="text"
                value={newEventTitle}
                maxLength={200}
                disabled={newEventBusy}
                onInput={(e) => setNewEventTitle((e.target as HTMLInputElement).value)}
              />
            </label>
            <div class="cockcal__newevent-row">
              <label class="cockcal__newevent-field">
                <span>Date</span>
                <input
                  type="date"
                  value={newEventDate}
                  required
                  disabled={newEventBusy}
                  onInput={(e) => setNewEventDate((e.target as HTMLInputElement).value)}
                />
              </label>
              <label class="cockcal__newevent-field">
                <span>Time</span>
                <input
                  type="time"
                  value={newEventTime}
                  required
                  disabled={newEventBusy}
                  onInput={(e) => setNewEventTime((e.target as HTMLInputElement).value)}
                />
              </label>
            </div>
            {newEventError ? (
              <div class="cockcal__newevent-error" role="alert">
                {newEventError}
              </div>
            ) : null}
            <div class="cockcal__newevent-actions">
              <button type="button" onClick={() => setNewEventOpen(false)} disabled={newEventBusy}>
                Discard
              </button>
              <button type="submit" class="cockcal__newevent-primary" disabled={newEventBusy || !newEventTitle.trim()}>
                {newEventBusy ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

// ===========================================================================
// Mist strip: mini-month + the calendar list, grouped by account.
// ===========================================================================

function CalendarStrip(props: {
  anchor: Date;
  today: Date;
  onJump: (d: Date) => void;
  dirByAccount: Map<string, CalEntry[]>;
  vowricEntry: CalEntry;
  hidden: Set<string>;
  onToggle: (key: string) => void;
}) {
  const { anchor, today, onJump, dirByAccount, vowricEntry, hidden, onToggle } = props;
  const gridStart = mondayOf(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  // ⚑ Atlas review (2026-10-16): the old 5-row/35-cell cap ("reads a month
  // cleanly") silently DROPPED a trailing day of the shown month whenever
  // that month's 6th row carried one of its own days rather than only the
  // next month's — e.g. November 2026 starts on a Sunday, so Nov 30 falls
  // in row 6 and was never in the 35 cells at all. ALWAYS 42 (6 rows),
  // the SAME cell count the main Month grid already uses (monthCells
  // below) — every day of the shown month is guaranteed present, and the
  // two grids can never show a different number of weeks for the same
  // month.
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  return (
    <nav class="cockcal__strip" aria-label="Calendars">
      <div class="cockcal__mini">
        <div class="cockcal__mini-title">{anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</div>
        <div class="cockcal__mini-grid">
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <span key={i} class="cockcal__mini-dow">
              {d}
            </span>
          ))}
          {cells.map((d) => {
            const inMonth = d.getMonth() === anchor.getMonth();
            const isToday = sameDay(d, today);
            const isAnchor = sameDay(d, anchor);
            return (
              <button
                key={d.toISOString()}
                type="button"
                class={`cockcal__mini-day${isAnchor ? ' is-anchor' : ''}${isToday ? ' is-today' : ''}${!inMonth ? ' is-outside' : ''}`}
                aria-label={d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}
                onClick={() => onJump(d)}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>
      <div class="cockcal__cal-list">
        <div class="cockcal__cal-group-label">Calendars</div>
        {[...dirByAccount.entries()].map(([email, cals]) => (
          <div class="cockcal__cal-group" key={email}>
            <div class="cockcal__account-label">{email}</div>
            {cals.map((c) => (
              <CalRow key={c.key} entry={c} checked={!hidden.has(c.key)} onToggle={() => onToggle(c.key)} />
            ))}
          </div>
        ))}
        <div class="cockcal__cal-group">
          <CalRow entry={vowricEntry} checked={!hidden.has(vowricEntry.key)} onToggle={() => onToggle(vowricEntry.key)} />
        </div>
      </div>
    </nav>
  );
}

function CalRow({ entry, checked, onToggle }: { entry: CalEntry; checked: boolean; onToggle: () => void }) {
  return (
    <label class="cockcal__cal-row">
      <input type="checkbox" checked={checked} onChange={onToggle} style={{ accentColor: entry.color }} />
      <span class="cockcal__cal-swatch" style={{ background: entry.color }} aria-hidden="true" />
      <span class="cockcal__cal-name">{entry.label}</span>
      {entry.readOnly ? (
        <span class="cockcal__cal-readonly" title="Read-only in Google" aria-label="Read-only">
          🔒
        </span>
      ) : null}
      <span class="cockcal__cal-tag">{entry.tag}</span>
    </label>
  );
}

// ===========================================================================
// Day / Week time grid.
// ===========================================================================

function TimeGrid(props: {
  days: Date[];
  today: Date;
  hours: number[];
  nowMin: number;
  eventsOn: (d: Date) => Array<{ e: CalendarEventApi; span: EventSpan; dir: CalEntry | undefined }>;
  drag: { eventId: string; startMin: number; durationMin: number } | null;
  selectedId: string | null;
  onPickEvent: (id: string) => void;
  onPickDay?: (d: Date) => void;
  onBeginDrag: (e: PointerEvent, ev: CalendarEventApi, mode: 'move' | 'resize') => void;
  compactGutter: boolean;
}) {
  const { days, today, hours, nowMin, eventsOn, drag, selectedId, onPickEvent, onPickDay, onBeginDrag, compactGutter } = props;
  const gridHeight = (HOUR_END - HOUR_START) * PX_PER_HOUR;
  // ⚑ atlas-core review (10-15): 16px cut hour digits and "all-day" off at
  // the gutter's left edge. 20px is still inside the fold-open budget — at
  // 900px total (580px of content beside the 320px Atlas column), 7
  // columns at the 80px floor need only 560px, leaving 20px for the
  // gutter exactly (580-560=20) — see TimeGrid's own measured widths in
  // the brief's report.
  const gutter = compactGutter ? 20 : 56;
  return (
    <div
      class={`cockcal__timegrid${compactGutter ? ' is-compact' : ''}`}
      style={{ '--cockcal-gutter': `${gutter}px`, '--cockcal-cols': String(days.length) } as Record<string, string>}
    >
      <div class="cockcal__gutter-head" />
      {days.map((d) => {
        const isToday = sameDay(d, today);
        return (
          <button
            type="button"
            key={d.toISOString()}
            class="cockcal__daycol-head"
            onClick={() => onPickDay?.(d)}
            disabled={!onPickDay}
          >
            <span class="cockcal__dow">{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
            <span class={`cockcal__daynum${isToday ? ' is-today' : ''}`}>{d.getDate()}</span>
          </button>
        );
      })}

      <div class="cockcal__allday-label">{compactGutter ? (
        <>
          <span>all</span>
          <span>day</span>
        </>
      ) : 'all-day'}</div>
      {days.map((d) => (
        <div class="cockcal__allday-cell" key={d.toISOString()}>
          {eventsOn(d)
            .filter((x) => x.span.allDay)
            .map((x) => (
              <button
                type="button"
                key={x.e.id}
                class="cockcal__allday-chip"
                style={{ '--cal': x.dir?.color ?? VOWRIC_COLOR } as Record<string, string>}
                onClick={() => onPickEvent(x.e.id)}
              >
                {x.e.status === 'cancelled' ? `Cancelled: ${x.e.title ?? ''}` : x.e.title}
              </button>
            ))}
        </div>
      ))}

      <div class="cockcal__gutter" style={{ height: `${gridHeight}px` }}>
        {hours.map((h) => (
          <span key={h} class="cockcal__hour-label" style={{ top: `${(h - HOUR_START) * PX_PER_HOUR}px` }}>
            {compactGutter ? String(h) : `${String(h).padStart(2, '0')}:00`}
          </span>
        ))}
      </div>
      {days.map((d) => {
        const isToday = sameDay(d, today);
        return (
          <div class="cockcal__daycol" key={d.toISOString()} style={{ height: `${gridHeight}px` }}>
            {eventsOn(d)
              .filter((x) => !x.span.allDay)
              .map(({ e, span, dir }) => {
                const isDragging = drag && drag.eventId === e.id;
                const startMin = isDragging ? drag!.startMin : span.startMin;
                const durationMin = isDragging ? drag!.durationMin : span.durationMin;
                const top = (startMin - HOUR_START * 60) * PX_PER_MIN;
                const height = Math.max(18, durationMin * PX_PER_MIN - 2);
                const color = dir?.color ?? VOWRIC_COLOR;
                const writable = dir ? dir.writable : e.source === 'authored';
                const cancelled = e.status === 'cancelled';
                // ⚑ atlas-core review round 3 (10-15): measured against the
                // REAL rendered line-height/gap/padding (eventLayoutFor),
                // never a hand-estimated constant — see that function's own
                // header for why the estimate produced half-visible lines.
                const { titleLines, showTime } = eventLayoutFor(height);
                return (
                  <div
                    key={e.id}
                    class={`cockcal__event${selectedId === e.id ? ' is-selected' : ''}${cancelled ? ' is-cancelled' : ''}`}
                    style={
                      {
                        top: `${top}px`,
                        height: `${height}px`,
                        '--cal': color,
                      } as Record<string, string>
                    }
                    onPointerDown={(ev) => (writable && !cancelled ? onBeginDrag(ev as unknown as PointerEvent, e, 'move') : undefined)}
                    onClick={() => onPickEvent(e.id)}
                    data-testid={`event-${e.id}`}
                  >
                    <span class="cockcal__event-title" style={{ WebkitLineClamp: String(titleLines) }}>
                      {cancelled ? `Cancelled: ${e.title ?? ''}` : e.title ?? '(untitled)'}
                    </span>
                    {showTime ? (
                      <span class="cockcal__event-time">
                        {String(Math.floor(startMin / 60)).padStart(2, '0')}:{String(startMin % 60).padStart(2, '0')}
                      </span>
                    ) : null}
                    {writable && !cancelled ? (
                      <span
                        class="cockcal__event-resize"
                        onPointerDown={(ev) => {
                          ev.stopPropagation();
                          onBeginDrag(ev as unknown as PointerEvent, e, 'resize');
                        }}
                        aria-hidden="true"
                      />
                    ) : null}
                  </div>
                );
              })}
            {isToday && nowMin >= HOUR_START * 60 && nowMin <= HOUR_END * 60 ? (
              <div class="cockcal__now-line" style={{ top: `${(nowMin - HOUR_START * 60) * PX_PER_MIN}px` }} aria-hidden="true" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ===========================================================================
// Month grid — Monday-first, three items then "+N more".
// ===========================================================================

function MonthGrid(props: {
  cells: Date[];
  month: number;
  today: Date;
  eventsOn: (d: Date) => Array<{ e: CalendarEventApi; span: EventSpan; dir: CalEntry | undefined }>;
  dirByKey: Map<string, CalEntry>;
  onPickDay: (d: Date) => void;
  onPickEvent: (id: string) => void;
}) {
  const { cells, month, today, eventsOn, onPickDay, onPickEvent } = props;
  // ⚑ Atlas review (2026-10-16): `cells` is already trimmed to the real
  // week count (35 or 42 — see monthCells' own header); this grid's rows
  // share the available height EVENLY between exactly that many, so a
  // 5-week month's rows are taller and a 6-week month's are shorter,
  // rather than either one overflowing or leaving a dead row.
  const weeks = Math.max(1, Math.round(cells.length / 7));
  return (
    <div class="cockcal__month">
      <div class="cockcal__month-dow">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div class="cockcal__month-grid" style={{ '--cockcal-weeks': String(weeks) } as Record<string, string>}>
        {cells.map((d) => {
          const inMonth = d.getMonth() === month;
          const isToday = sameDay(d, today);
          const items = eventsOn(d).sort((a, b) => (a.span.allDay ? -1 : a.span.startMin) - (b.span.allDay ? -1 : b.span.startMin));
          const shown = items.slice(0, 3);
          const more = items.length - 3;
          return (
            <div class={`cockcal__month-cell${inMonth ? '' : ' is-outside'}`} key={d.toISOString()}>
              <button type="button" class={`cockcal__month-daynum${isToday ? ' is-today' : ''}`} onClick={() => onPickDay(d)}>
                {d.getDate()}
              </button>
              {shown.map(({ e, span, dir }) => (
                <button type="button" key={e.id} class="cockcal__month-item" onClick={() => onPickEvent(e.id)}>
                  <span class="cockcal__month-dot" style={{ background: dir?.color ?? VOWRIC_COLOR }} aria-hidden="true" />
                  {!span.allDay ? (
                    <span class="cockcal__month-time">
                      {String(Math.floor(span.startMin / 60)).padStart(2, '0')}:{String(span.startMin % 60).padStart(2, '0')}
                    </span>
                  ) : null}
                  <span class="cockcal__month-item-title">{e.status === 'cancelled' ? `Cancelled: ${e.title ?? ''}` : e.title}</span>
                </button>
              ))}
              {more > 0 ? (
                <button type="button" class="cockcal__month-more" onClick={() => onPickDay(d)}>
                  +{more} more
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================================
// Detail bar.
// ===========================================================================

function DetailBar(props: {
  event: CalendarEventApi;
  entry: CalEntry | null;
  writable: boolean;
  busy: boolean;
  editing: boolean;
  draft: { title: string; location: string };
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onDraftChange: (d: { title: string; location: string }) => void;
  onSaveEdit: () => void;
  onCancelEvent?: () => void;
  onProposeToOrganizer?: () => void;
  onClose: () => void;
}) {
  const { event, entry, writable, busy, editing, draft, onStartEdit, onCancelEdit, onDraftChange, onSaveEdit, onCancelEvent, onProposeToOrganizer, onClose } = props;
  const span = spanOf(event);
  const when = span
    ? span.allDay
      ? 'All day'
      : `${String(Math.floor(span.startMin / 60)).padStart(2, '0')}:${String(span.startMin % 60).padStart(2, '0')}`
    : '';
  const calLabel = entry ? `${entry.label}${entry.accountEmail ? ` · ${entry.accountEmail}` : ''}` : 'Vowric';
  const sourceLine =
    event.source === 'google'
      ? entry?.readOnly
        ? 'From Google — read-only here'
        : 'From Google'
      : event.source === 'authored'
      ? 'Vowric · also shown in Google'
      : 'From Google';

  return (
    <div class="cockcal__detail" role="region" aria-label="Event detail">
      <span class="cockcal__detail-dot" style={{ background: entry?.color ?? VOWRIC_COLOR }} aria-hidden="true" />
      <div class="cockcal__detail-body">
        {editing ? (
          <>
            <input
              class="cockcal__detail-input"
              value={draft.title}
              onInput={(e) => onDraftChange({ ...draft, title: (e.target as HTMLInputElement).value })}
              placeholder="Title"
            />
            <input
              class="cockcal__detail-input"
              value={draft.location}
              onInput={(e) => onDraftChange({ ...draft, location: (e.target as HTMLInputElement).value })}
              placeholder="Location"
            />
          </>
        ) : (
          <>
            <span class="cockcal__detail-title">{event.status === 'cancelled' ? `Cancelled: ${event.title ?? ''}` : event.title ?? '(untitled)'}</span>
            <span class="cockcal__detail-meta">
              {when} · {calLabel}
              {event.location ? ` · ${event.location}` : ''}
            </span>
          </>
        )}
      </div>
      <span class="cockcal__detail-source">{sourceLine}</span>
      {editing ? (
        <>
          <button type="button" class="cockcal__primary" disabled={busy} onClick={onSaveEdit}>
            Save
          </button>
          <button type="button" onClick={onCancelEdit}>
            Cancel
          </button>
        </>
      ) : (
        <>
          {writable ? (
            <button type="button" class="cockcal__primary" disabled={busy} onClick={onStartEdit}>
              Edit
            </button>
          ) : onProposeToOrganizer ? (
            <button type="button" disabled={busy} onClick={onProposeToOrganizer}>
              Ask the organizer
            </button>
          ) : null}
          {onCancelEvent ? (
            <button type="button" disabled={busy} onClick={onCancelEvent}>
              Cancel event
            </button>
          ) : null}
        </>
      )}
      <button type="button" onClick={onClose} aria-label="Close">
        Close
      </button>
    </div>
  );
}

// ===========================================================================
// "Tell the guests?" — no preselected answer; dismissing sends nothing.
// ===========================================================================

function TellGuestsPrompt(props: { message: string; busy: boolean; onTell: () => void; onDontTell: () => void; onDismiss: () => void }) {
  const { message, busy, onTell, onDontTell, onDismiss } = props;
  return (
    <div class="cockcal__ask" role="alertdialog" aria-label="Tell the guests?">
      {/* The SERVER's own sentence (voice canon §2.4) — never a second,
       * vaguer copy composed here. */}
      <p>{message}</p>
      <div class="cockcal__ask-actions">
        <button type="button" disabled={busy} onClick={onTell}>
          Tell them
        </button>
        <button type="button" disabled={busy} onClick={onDontTell}>
          Don’t tell
        </button>
        <button type="button" disabled={busy} onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      </div>
    </div>
  );
}
